export interface AppVersion {
  version: string;
  buildVersion: string;
  buildTimestamp: string;
  isDevelopment: boolean;
}

const UPDATE_CHECK_INTERVAL = 10 * 60 * 1000;

export const getAppVersion = (): AppVersion => ({
  version: import.meta.env.VITE_APP_VERSION || "unknown",
  buildVersion: import.meta.env.VITE_BUILD_VERSION || "unknown",
  buildTimestamp: import.meta.env.VITE_BUILD_TIMESTAMP || "unknown",
  isDevelopment: import.meta.env.DEV || false,
});

export const getVersionString = (): string => {
  const v = getAppVersion();
  return v.isDevelopment ? `${v.version}-dev` : v.buildVersion;
};

export const initAutoUpdate = (): { cleanup: () => void } => {
  if (!("serviceWorker" in navigator)) {
    return { cleanup: () => {} };
  }

  const abortController = new AbortController();
  let updateCheckTimeout: number | undefined;
  let reloading = false;

  const hadController = !!navigator.serviceWorker.controller;

  const reload = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => {
      if (hadController) reload();
    },
    { signal: abortController.signal },
  );

  const checkForUpdates = async () => {
    if (reloading) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      await reg?.update();
    } catch {
      // ignore
    }
  };

  checkForUpdates();

  document.addEventListener(
    "visibilitychange",
    () => {
      if (!document.hidden) checkForUpdates();
    },
    { signal: abortController.signal },
  );

  const scheduleCheck = () => {
    updateCheckTimeout = window.setTimeout(async () => {
      await checkForUpdates();
      scheduleCheck();
    }, UPDATE_CHECK_INTERVAL);
  };
  scheduleCheck();

  return {
    cleanup: () => {
      abortController.abort();
      clearTimeout(updateCheckTimeout);
    },
  };
};
